import { useCallback, useRef, useState } from 'react'
import type { GridApi } from 'ag-grid-community'

import { useUpdateField } from '@/services/mutations'
import type { Context } from '@/types/grid'

import type { EditorProperty } from './types'

type UseInlineEditParams = {
  stopEditing: () => void
  canUpdate: boolean
  context?: Context
  property?: EditorProperty
  api?: GridApi<unknown>
}

type UseInlineEditReturn = {
  isEditing: boolean
  editValue: string
  setEditValue: (value: string) => void
  startEditing: (optionLabel: string, optionId: string) => void
  cancelEditing: () => void
  submitEdit: () => void
  handleKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
}

/**
 * Patch all mutable grid-context caches that map option ids to labels,
 * then force AG Grid to re-render the affected column so cells show
 * the new label immediately without waiting for a query refetch.
 */
const syncGridAfterRename = (
  context: Context,
  propertyKey: string,
  optionId: string,
  newLabel: string,
  isRef: boolean,
  api?: GridApi<unknown>,
) => {
  if (!isRef) {
    const listOptions = context.propertyOptions?.[propertyKey]
    if (Array.isArray(listOptions)) {
      Reflect.set(context, 'propertyOptions', {
        ...context.propertyOptions,
        [propertyKey]: listOptions.map((opt) =>
          opt.value === optionId || opt.id === optionId
            ? { ...opt, label: newLabel }
            : opt,
        ),
      })
    }
  }

  const refDataByKey = context.refDataByKey
  const existingById = refDataByKey?.[propertyKey]
  if (refDataByKey && existingById) {
    Reflect.set(context, 'refDataByKey', {
      ...refDataByKey,
      [propertyKey]: { ...existingById, [optionId]: newLabel },
    })
  }

  const optionsByID = context.optionsByID
  const lookupById = optionsByID?.[propertyKey]
  const existing = lookupById?.[optionId]
  if (optionsByID && lookupById && existing) {
    Reflect.set(context, 'optionsByID', {
      ...optionsByID,
      [propertyKey]: {
        ...lookupById,
        [optionId]: { ...existing, label: newLabel },
      },
    })
  }

  api?.refreshCells({ force: true, columns: [propertyKey] })
}

/**
 * Hook that encapsulates inline editing logic for option labels
 * Handles edit state, keyboard interactions, and backend persistence
 */
export const useInlineEdit = ({
  stopEditing,
  canUpdate,
  context,
  property,
  api,
}: UseInlineEditParams): UseInlineEditReturn => {
  const { mutate: updateField } = useUpdateField()

  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState('')

  const originalLabelRef = useRef('')
  const optionIdRef = useRef('')

  const startEditing = useCallback(
    (optionLabel: string, optionId: string) => {
      if (!canUpdate) return

      originalLabelRef.current = optionLabel
      optionIdRef.current = optionId
      setEditValue(optionLabel)
      setIsEditing(true)
    },
    [canUpdate],
  )

  const cancelEditing = useCallback(() => {
    setEditValue(originalLabelRef.current)
    setIsEditing(false)
    stopEditing()
  }, [stopEditing])

  const submitEdit = useCallback(() => {
    const originalLabel = originalLabelRef.current
    const optionId = optionIdRef.current
    const changed = editValue !== originalLabel && editValue.trim() !== ''

    if (changed && optionId) {
      updateField({ id: optionId, label: editValue })

      const propertyKey = property?.key
      if (context && propertyKey) {
        syncGridAfterRename(
          context,
          propertyKey,
          optionId,
          editValue,
          property?.isRef ?? false,
          api,
        )
      }
    }

    setIsEditing(false)
    stopEditing()
  }, [editValue, stopEditing, updateField, context, property, api])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        submitEdit()
      } else if (event.key === 'Escape') {
        event.preventDefault()
        cancelEditing()
      }
    },
    [submitEdit, cancelEditing],
  )

  return {
    isEditing,
    editValue,
    setEditValue,
    startEditing,
    cancelEditing,
    submitEdit,
    handleKeyDown,
  }
}
